/**
 * AI Assistant Provider
 *
 * Context provider for AI Assistant state management.
 * Provides state and actions to all child components.
 */

import {
  type Accessor,
  type ParentComponent,
  createContext,
  createEffect,
  createSignal,
  useContext,
} from 'solid-js';
import type {
  AssistantContext,
  AssistantState,
  Conversation,
  ConversationSummary,
  Message,
} from './types';

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// Storage key for localStorage
const STORAGE_KEY = 'aigents_assistant_state';
const CONVERSATIONS_KEY = 'aigents_assistant_conversations';

// Default initial state
const initialState: AssistantState = {
  isOpen: false,
  isLoading: false,
  conversationId: null,
  messages: [],
  currentContext: { page: '/' },
  error: null,
};

// Load state from localStorage
function loadPersistedState(): Partial<AssistantState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        conversationId: parsed.conversationId || null,
        isOpen: parsed.isOpen || false,
      };
    }
  } catch {
    // Ignore errors
  }
  return {};
}

// Save state to localStorage
function persistState(state: Pick<AssistantState, 'conversationId' | 'isOpen'>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore errors
  }
}

// Load conversations from localStorage
function loadConversations(): Map<string, Conversation> {
  try {
    const stored = localStorage.getItem(CONVERSATIONS_KEY);
    if (stored) {
      const arr = JSON.parse(stored) as Conversation[];
      return new Map(arr.map((c) => [c.id, c]));
    }
  } catch {
    // Ignore errors
  }
  return new Map();
}

// Save conversations to localStorage
function saveConversations(conversations: Map<string, Conversation>): void {
  try {
    const arr = Array.from(conversations.values());
    // Keep only last 50 conversations
    const sorted = arr.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 50);
    localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(sorted));
  } catch {
    // Ignore errors
  }
}

// Context type
interface AssistantContextValue {
  // State accessors
  state: Accessor<AssistantState>;
  conversations: Accessor<ConversationSummary[]>;

  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  setContext: (context: AssistantContext) => void;
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  appendContent: (messageId: string, content: string) => void;
  clearMessages: () => void;
  newConversation: () => void;
  loadConversation: (conversationId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const AssistantCtx = createContext<AssistantContextValue>();

export const AssistantProvider: ParentComponent = (props) => {
  // Load persisted state
  const persistedState = loadPersistedState();

  // State signals
  const [state, setState] = createSignal<AssistantState>({
    ...initialState,
    ...persistedState,
  });

  // Conversations storage
  const [conversationsMap, setConversationsMap] = createSignal(loadConversations());

  // Derived: conversation summaries for UI
  const conversations = (): ConversationSummary[] => {
    const map = conversationsMap();
    return Array.from(map.values())
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map((c) => ({
        id: c.id,
        title: c.title,
        lastMessage: c.messages[c.messages.length - 1]?.content.slice(0, 100) || '',
        updatedAt: c.updatedAt,
      }));
  };

  // Load messages for current conversation
  createEffect(() => {
    const convId = state().conversationId;
    if (convId) {
      const conv = conversationsMap().get(convId);
      if (conv) {
        setState((s) => ({ ...s, messages: conv.messages }));
      }
    }
  });

  // Persist state changes
  createEffect(() => {
    const s = state();
    persistState({ conversationId: s.conversationId, isOpen: s.isOpen });
  });

  // Save conversations when they change
  createEffect(() => {
    saveConversations(conversationsMap());
  });

  // Actions
  const toggle = () => {
    setState((s) => ({ ...s, isOpen: !s.isOpen }));
  };

  const open = () => {
    setState((s) => ({ ...s, isOpen: true }));
  };

  const close = () => {
    setState((s) => ({ ...s, isOpen: false }));
  };

  const setContext = (context: AssistantContext) => {
    setState((s) => ({ ...s, currentContext: context }));
  };

  const setLoading = (loading: boolean) => {
    setState((s) => ({ ...s, isLoading: loading }));
  };

  const setError = (error: string | null) => {
    setState((s) => ({ ...s, error }));
  };

  const addMessage = (message: Message) => {
    setState((s) => {
      const messages = [...s.messages, message];

      // Update or create conversation
      let convId = s.conversationId;
      if (!convId) {
        convId = generateId();
      }

      setConversationsMap((map) => {
        const newMap = new Map(map);
        const existing = newMap.get(convId!);
        newMap.set(convId!, {
          id: convId!,
          title:
            existing?.title ||
            (message.role === 'user' ? message.content.slice(0, 50) : 'New conversation'),
          messages,
          createdAt: existing?.createdAt || Date.now(),
          updatedAt: Date.now(),
        });
        return newMap;
      });

      return { ...s, messages, conversationId: convId };
    });
  };

  const updateMessage = (messageId: string, updates: Partial<Message>) => {
    setState((s) => {
      const messages = s.messages.map((m) => (m.id === messageId ? { ...m, ...updates } : m));

      // Update conversation
      if (s.conversationId) {
        setConversationsMap((map) => {
          const newMap = new Map(map);
          const existing = newMap.get(s.conversationId!);
          if (existing) {
            newMap.set(s.conversationId!, { ...existing, messages, updatedAt: Date.now() });
          }
          return newMap;
        });
      }

      return { ...s, messages };
    });
  };

  const appendContent = (messageId: string, content: string) => {
    setState((s) => {
      const messages = s.messages.map((m) =>
        m.id === messageId ? { ...m, content: m.content + content } : m,
      );

      // Update conversation
      if (s.conversationId) {
        setConversationsMap((map) => {
          const newMap = new Map(map);
          const existing = newMap.get(s.conversationId!);
          if (existing) {
            newMap.set(s.conversationId!, { ...existing, messages, updatedAt: Date.now() });
          }
          return newMap;
        });
      }

      return { ...s, messages };
    });
  };

  const clearMessages = () => {
    setState((s) => ({ ...s, messages: [], conversationId: null }));
  };

  const newConversation = () => {
    setState((s) => ({ ...s, messages: [], conversationId: null, error: null }));
  };

  const loadConversation = (conversationId: string) => {
    const conv = conversationsMap().get(conversationId);
    if (conv) {
      setState((s) => ({ ...s, conversationId, messages: conv.messages, error: null }));
    }
  };

  // Send message - handles streaming from backend
  const sendMessage = async (content: string) => {
    if (!content.trim() || state().isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now(),
    };
    addMessage(userMessage);

    // Create placeholder assistant message
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
    };
    addMessage(assistantMessage);

    setLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('supabase.auth.token') || ''}`,
        },
        body: JSON.stringify({
          message: content.trim(),
          conversationId: state().conversationId,
          context: state().currentContext,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Handle SSE stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);

              switch (event.type) {
                case 'text':
                  appendContent(assistantMessageId, event.content || '');
                  break;

                case 'tool_call':
                  updateMessage(assistantMessageId, {
                    toolCalls: [
                      ...(state().messages.find((m) => m.id === assistantMessageId)?.toolCalls ||
                        []),
                      event.toolCall,
                    ],
                  });
                  break;

                case 'tool_result':
                  updateMessage(assistantMessageId, {
                    toolResults: [
                      ...(state().messages.find((m) => m.id === assistantMessageId)?.toolResults ||
                        []),
                      event.toolResult,
                    ],
                  });
                  break;

                case 'entity':
                  updateMessage(assistantMessageId, {
                    entities: [
                      ...(state().messages.find((m) => m.id === assistantMessageId)?.entities ||
                        []),
                      event.entity,
                    ],
                  });
                  break;

                case 'error':
                  setError(event.error || 'An error occurred');
                  break;
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }

      // Mark streaming as complete
      updateMessage(assistantMessageId, { isStreaming: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send message';
      setError(message);
      updateMessage(assistantMessageId, {
        isStreaming: false,
        content:
          state().messages.find((m) => m.id === assistantMessageId)?.content ||
          'Sorry, I encountered an error. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const value: AssistantContextValue = {
    state,
    conversations,
    toggle,
    open,
    close,
    setContext,
    sendMessage,
    addMessage,
    updateMessage,
    appendContent,
    clearMessages,
    newConversation,
    loadConversation,
    setLoading,
    setError,
  };

  return <AssistantCtx.Provider value={value}>{props.children}</AssistantCtx.Provider>;
};

export function useAssistant(): AssistantContextValue {
  const context = useContext(AssistantCtx);
  if (!context) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
}
